import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import type { TriggerColumn } from '../../lib/automation-model';

interface TriggerColumnSelectProps {
  /** The field's visible label, which names the picker for a screen reader. */
  label: string;
  value: string;
  onValueChange: (columnId: string) => void;
  columns: TriggerColumn[];
  placeholder: string;
  /**
   * Heading over the record's own fields, or null on a board that has none, where
   * every entry is the board's and a heading would only add noise.
   */
  recordHeading: string | null;
}

/**
 * Picks the column a trigger watches. A board column and a record field can
 * share a name ("Status"), so on a record board each list sits under its own
 * heading rather than as two identical entries.
 */
export function TriggerColumnSelect({
  label,
  value,
  onValueChange,
  columns,
  placeholder,
  recordHeading,
}: TriggerColumnSelectProps) {
  const { t } = useTranslation('trackers');
  const boardColumns = columns.filter((c) => c.source !== 'record');
  const recordColumns = columns.filter((c) => c.source === 'record');

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="h-9" aria-label={label}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {recordHeading === null ? (
          <ColumnItems columns={columns} />
        ) : (
          <>
            <ColumnGroup
              heading={t('trackersBoard.automationRuleFields.boardColumns')}
              columns={boardColumns}
            />
            <ColumnGroup heading={recordHeading} columns={recordColumns} />
          </>
        )}
      </SelectContent>
    </Select>
  );
}

function ColumnGroup({ heading, columns }: { heading: string; columns: TriggerColumn[] }) {
  if (columns.length === 0) return null;
  return (
    <SelectGroup>
      <SelectLabel>{heading}</SelectLabel>
      <ColumnItems columns={columns} />
    </SelectGroup>
  );
}

function ColumnItems({ columns }: { columns: TriggerColumn[] }) {
  return columns.map((c) => (
    <SelectItem key={c.id} value={c.id}>
      {c.label}
    </SelectItem>
  ));
}
